const libPictSectionForm = require(`pict-section-form`);
//const PictViewClass = require('pict-view');


const defaultViewConfiguration = (
	{
		ViewIdentifier: 'Pict-Form-Solver',
		DefaultRenderable: 'HL-Pict-Form-Solver-Renderable',
		DefaultDestinationAddress: '#Pict-Form-Solver-In-Panel-Wrapper',
		RenderOnLoad: false,
		CSS: /*css*/`
            #Pict-Form-Solver-Content {
                color: var(--panels-content-block-text-color);
            }
            /* ── Filter bar ──────────────────────────────────────────
               Sticky search input above the solver entries. Sticky so
               the user keeps the filter affordance in view while
               scrolling through long log lists. The clear (×) button
               and the "no matches" banner are toggled in JS via the
               .is-active class set on their parents. */
            .PSFS-Filter-Bar {
                position: sticky;
                top: 0;
                z-index: 5;
                display: flex;
                align-items: center;
                gap: 0.55rem;
                padding: 0.5rem 0.65rem;
                background-color: var(--bulma-black-bis);
                border-bottom: 1px solid var(--panels-content-block-divider-color);
            }
            /* The Solve button shares the bar with the filter input — pin
               its size so it doesn't shrink when the panel narrows, and
               keep a clear visual gap so the action button doesn't read
               as part of the filter affordance to its right. */
            .PSFS-Filter-Solve {
                flex: 0 0 auto;
            }
            .PSFS-Filter-Bar .control {
                flex: 1 1 auto;
                position: relative;
            }
            /* Match counter on the right of the filter bar. Always
               visible so the user gets a feel for total log volume
               even before they start typing. The leading filter icon
               anchors the column visually; tabular-nums keeps digits
               aligned as the count ticks down character-by-character. */
            .PSFS-Filter-Counter {
                flex: 0 0 auto;
                display: inline-flex;
                align-items: center;
                gap: 0.35rem;
                font-size: 0.72rem;
                color: var(--bulma-text-60);
                font-variant-numeric: tabular-nums;
                white-space: nowrap;
                letter-spacing: 0.02em;
            }
            .PSFS-Filter-Counter .icon {
                font-size: 0.75rem;
                color: inherit;
            }
            .PSFS-Filter-Bar.is-active .PSFS-Filter-Counter {
                color: var(--bulma-warning);
            }
            /* In-place highlighting of matched substrings, à la browser
               Find. The mark element is wrapped around each token hit
               in visible entries by _applyHighlightsTo(). Warning-yellow
               background reads as "active filter highlight" without
               competing with the existing severity-* tones used by
               custom log entries. */
            .PSFS-Filter-Highlight {
                background-color: var(--bulma-warning);
                color: var(--bulma-black);
                padding: 0 0.1rem;
                border-radius: 0.15rem;
                font-weight: 600;
                /* Pict's solver-entry rule uses !important on color to
                   override Pict defaults — we inherit-and-override here
                   so highlights stay visible inside the warning-tinted
                   .PSFExt-Solver-Entry expression band, monospace token
                   chips, and severity-tinted custom log rows alike. */
                box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
            }
            .PSFS-Filter-Input {
                width: 100%;
                padding: 0.4rem 2rem 0.4rem 2rem;
                background-color: var(--panels-input-background-color) !important;
                color: var(--panels-input-text-color) !important;
                border: 1px solid var(--panels-input-border-color) !important;
                border-radius: 0.25rem;
                font-size: 0.85rem;
                line-height: 1.3;
            }
            .PSFS-Filter-Input:focus {
                outline: none;
                border-color: var(--bulma-warning) !important;
                box-shadow: 0 0 0 2px rgba(255, 184, 0, 0.18);
            }
            .PSFS-Filter-Icon {
                position: absolute;
                left: 0.55rem;
                top: 50%;
                transform: translateY(-50%);
                color: var(--bulma-text-60);
                font-size: 0.8rem;
                pointer-events: none;
            }
            .PSFS-Filter-Clear {
                position: absolute;
                right: 0.3rem;
                top: 50%;
                transform: translateY(-50%);
                color: var(--bulma-text-60);
                background: transparent;
                border: 0;
                padding: 0.2rem 0.35rem;
                cursor: pointer;
                border-radius: 0.2rem;
                display: none;
                font-size: 0.8rem;
                line-height: 1;
            }
            .PSFS-Filter-Clear:hover {
                color: var(--bulma-text-95);
                background: rgba(255, 255, 255, 0.06);
            }
            .PSFS-Filter-Bar.is-active .PSFS-Filter-Clear {
                display: inline-flex;
                align-items: center;
            }
            /* Hide-by-filter rule. Used in addition to (not instead of)
               Bulma's .is-hidden so we don't fight collapsible-section
               state — the filter and the user's manual collapse remain
               independently controllable. */
            .PSFS-Filter-Hidden {
                display: none !important;
            }
            .PSFS-Filter-Empty {
                padding: 1.5rem 1rem;
                text-align: center;
                color: var(--bulma-text-60);
                font-size: 0.85rem;
                display: none;
            }
            .PSFS-Filter-Empty.is-active {
                display: block;
            }
            /* Override Bulma's .panel.is-warning tinting on the panel body
               so the Solver pane matches the dark Validations panel. The
               gold .panel-heading is preserved because Bulma themes that
               independently via the heading-specific rule. */
            #HeadlightPanels-SolverPanel-Content {
                background-color: var(--panels-background-color) !important;
            }

            /* ── Standard solver entries (Pict-rendered solver list) ───
               One .PSFExt-Section per solver expression. The caps dark
               header band sits on top with an 8px warning-yellow leading
               border; content rows beneath get an 8px black-ter leading
               border so the eye reads "yellow = parent, gray = children".
               Sections stack flush.
               Pict's support base also ships defaults that paint things
               light (.PSFExt-Section-ExtraData → #fafafa, .PSFExt-Solver-Entry
               → 1px dotted #ccc bottom border, etc) at the same selector
               specificity as our rules, so most overrides need !important
               to win the load-order tiebreak. */
            .PSFExt-Section {
                background-color: transparent !important;
                margin: 0 !important;
                padding: 0 !important;
                border-radius: 0 !important;
            }
            .PSFExt-Section-ExtraData {
                background-color: transparent !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .PSFExt-Section-Header {
                display: flex;
                align-items: center;
                gap: 0.4rem;
                padding: 0.5rem 0.65rem 0.5rem 0.25rem !important;
                margin: 0 !important;
                background-color: var(--bulma-grey-darker) !important;
                color: var(--bulma-text-95);
                font-size: 0.75rem !important;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                border-left: 8px solid var(--bulma-warning);
            }
            .PSFExt-Section-Header span.PSFExt-DeEmphasize {
                color: var(--bulma-text-65) !important;
                font-weight: 500;
                margin-left: 0.25rem;
                margin-right: 0.25rem;
            }
            /* Section header layout — meta on the left, actions hard-right —
               is handled by Bulma flex utility classes (.is-flex,
               .is-flex-grow-1, .ml-auto) directly on the markup. The
               Edit / More chips use Bulma's .button.is-small.is-dark so
               their look + hover comes from the framework. */

            /* ── Expression display (read-only) ────────────────────────
               This is the always-visible <p class="PSFExt-Solver-Entry">
               that shows the formula. Pict's default theme paints the
               text magenta; we override to a softer dark overlay that
               reads as a code block while still feeling part of the
               panel's gray-tone palette (rather than a stark black band
               against the panel bg). */
            .PSFExt-Solver-Entry {
                word-wrap: break-word;
                margin: 0 !important;
                padding: 0.5rem 0.75rem 0.5rem 0.75rem !important;
                color: var(--bulma-warning-85) !important;
                background-color: rgba(0, 0, 0, 0.25) !important;
                font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace;
                font-size: 0.8rem;
                line-height: 1.5;
                border-bottom: none !important;
                border-left: 8px solid var(--bulma-black-ter) !important;
            }

            /* The expression editor textarea (shown after clicking Edit)
               picks up the panel's input theme so it feels native to the
               surrounding form fields. Pict's default sets a 100%-20px
               width and 80px min-height — overridden so the editor sits
               flush inside our row layout. */
            .PSFExt-ExpressionEditbox {
                width: 100% !important;
                min-height: 60px !important;
                padding: 0.5rem !important;
                background-color: var(--panels-input-background-color) !important;
                color: var(--panels-input-text-color) !important;
                border: 1px solid var(--panels-input-border-color) !important;
                border-radius: 0.25rem;
                font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace;
                font-size: 0.8rem;
                line-height: 1.5;
            }

            /* Small inline "Value:" / "Tokens:" rows under the expression.
               Pict's default sets margin-left, line-height: 0.85, and
               font-size: smaller — all overridden here so rows breathe. */
            .PSFExt-Section .PSFExt-Data {
                margin: 0 !important;
                padding: 0.35rem 0.65rem 0.35rem 0.75rem !important;
                font-size: 0.8rem !important;
                line-height: 1.5 !important;
                color: var(--panels-content-block-text-color);
                border-left: 8px solid var(--bulma-black-ter) !important;
            }
            /* Alternating row tint — slightly lighter wash on every other
               .PSFExt-Data row, matching the custom-logs alternating
               pattern. :nth-of-type counts among <p> siblings, so h5
               group dividers ("Postfix Tokens", "Virtual Symbols") don't
               break the alternation but the visual still feels banded. */
            .PSFExt-Section-ExtraData .PSFExt-Data:nth-of-type(even) {
                background-color: rgba(255, 255, 255, 0.04);
            }
            .PSFExt-Section .PSFExt-Label {
                color: var(--bulma-text-60);
                font-size: 0.7rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-right: 0.25rem;
            }

            /* ── Sub-section group headers ─────────────────────────────
               "Postfix Tokens" / "Virtual Symbols" / "Postfix Solve List"
               and the per-entry "Postfix Solve Entry #N Symbol …" bands.
               Sit one tier below the main section header in the visual
               hierarchy: still a caps band, but smaller and on a darker
               surface so the parent → children relationship reads.
               Pict ships defaults that paint these light (#ebebff /
               #fafafa); !important overrides keep the dark caps band. */
            .PSFExt-Content-Header {
                font-weight: 600;
                margin: 0 !important;
                padding: 0.35rem 0.65rem 0.35rem 0.75rem !important;
                background-color: var(--bulma-black-ter) !important;
                color: var(--bulma-text-85);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-size: 0.7rem;
                border-left: 8px solid var(--bulma-black-ter) !important;
            }
            .PSFExt-Content-Header.PSFExt-Section-Group-Header {
                font-size: 0.75rem !important;
            }
            .PSFExt-Content-Header.PSFExt-Data-Header {
                background-color: rgba(255, 255, 255, 0.04) !important;
                color: var(--bulma-text-75);
            }

            /* ── Data tables (postfix solve entry breakdowns) ──────────
               Caps headers, dark theme borders, monospace cells where
               appropriate via the Token chip styling on contained spans. */
            .PSFExt-Data-Table {
                margin: 0.25rem 0.5rem;
            }
            .PSFExt-Data-Table th {
                font-weight: 600;
                font-size: 0.7rem;
                padding: 0.4rem 0.5rem;
                color: var(--bulma-text-95);
                background-color: var(--bulma-grey-darker);
                text-transform: uppercase;
                letter-spacing: 0.05em;
                text-align: left;
                border-bottom: 1px solid var(--panels-content-block-divider-color);
            }
            .PSFExt-Data-Table td {
                word-wrap: break-word;
                padding: 0.35rem 0.5rem;
                font-size: 0.8rem;
                border: 1px solid var(--panels-content-block-divider-color);
                color: var(--panels-content-block-text-color);
            }
            .PSFExt-Data-Table td.cell-header {
                font-weight: 600;
                font-size: 0.7rem;
                color: var(--bulma-text-60);
                background-color: var(--panels-content-block-hover-background-color);
                text-transform: uppercase;
                letter-spacing: 0.05em;
            }

            /* ── Custom Solver Log styles ──────────────────────────────
               Section header / badge / collapse styling lives in
               panels-logs.scss (.hl-log-section, .hl-log-section-header,
               .hl-log-section-body) — shared with the Validations pane.
               Below are just the bits unique to the Custom Solver Logs:
               the wrapper banner, entry/calc rows, severity tags, and
               the variable-chip / numeric-formula display used inside
               calc rows. */
            .PSFS-CustomLog-Header {
                font-weight: bold;
                font-size: 1.1rem;
                padding: 0.5rem;
                color: var(--bulma-text-95);
                background-color: var(--bulma-black-ter);
                margin: 0;
            }
            .PSFS-CustomLog-Header .icon {
                color: var(--bulma-warning);
                margin-left: 0.5rem;
            }
            .PSFS-CustomLog-Entry {
                padding: 0.35rem 0.5rem 0.35rem 0.75rem;
                font-size: 0.85rem;
                border-left: 8px solid var(--bulma-black-ter);
                color: var(--panels-content-block-text-color);
            }
            .PSFS-CustomLog-Entry-Severity {
                font-weight: 600;
                text-transform: uppercase;
                font-size: 0.7rem;
                padding: 0.05rem 0.3rem;
                border-radius: 0.2rem;
                margin-right: 0.35rem;
            }
            .PSFS-CustomLog-Entry-Severity.severity-error {
                background-color: var(--bulma-danger-dark);
                color: var(--bulma-danger-light);
            }
            .PSFS-CustomLog-Entry-Severity.severity-warning {
                background-color: var(--bulma-warning-dark);
                color: var(--bulma-warning-light);
            }
            .PSFS-CustomLog-Entry-Severity.severity-info {
                background-color: var(--bulma-info-dark);
                color: var(--bulma-info-light);
            }
            .PSFS-CustomLog-Entry-Severity.severity-trace {
                background-color: var(--panels-content-block-hover-background-color);
                color: var(--bulma-text-60);
            }
            .PSFS-CustomLog-Calc {
                padding: 0.35rem 0.5rem 0.35rem 0.75rem;
                font-size: 0.85rem;
                border-left: 8px solid var(--bulma-black-ter);
                color: var(--panels-content-block-text-color);
            }
            .PSFS-CustomLog-Calc-Result {
                font-weight: 700;
                color: var(--bulma-info-light);
            }
            .PSFS-CustomLog-Calc-Formula {
                font-size: 0.8rem;
                color: var(--bulma-text-60);
                margin-top: 0.2rem;
                line-height: 1.6;
            }
            /* Inline pill — used for formula variable references in custom
               log calculations AND Pict's postfix-token names. Subtle
               blue tint so chips stay readable on the dark panel without
               the loud saturated tone we had before. !important on
               bg/color is needed for .PSFExt-Token to override Pict's
               default lime-green token color. */
            .PSFS-CustomLog-Chip,
            .PSFExt-Token {
                display: inline-block;
                padding: 0.1rem 0.4rem;
                background: rgba(78, 154, 220, 0.15) !important;
                color: #7db8e8 !important;
                font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace;
                font-size: 0.75rem;
                border: 1px solid rgba(78, 154, 220, 0.25);
                border-radius: 0.25rem;
                vertical-align: baseline;
            }
            .PSFS-CustomLog-Chip-Op {
                display: inline-block;
                font-size: 0.75rem;
                color: var(--bulma-text-65);
                padding: 0 0.2rem;
                vertical-align: baseline;
            }
            .PSFS-CustomLog-Calc-NumericFormula {
                font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace;
                font-size: 0.75rem;
                color: var(--bulma-text-65);
                margin-top: 0.1rem;
            }
        `,
		Templates: [
			{
				Hash: 'HL-Pict-Form-Solver-Content',
				Template: /*html*/`
<div id="Pict-Form-Solver-Content">
    <div id="PSFS-Filter-Bar" class="PSFS-Filter-Bar">
        <button type="button" class="button is-small is-primary PSFS-Filter-Solve"
            title="Re-run the solver"
            onclick="{~P~}.views.PictFormMetacontroller.solve(); {~P~}.views.PictFormMetacontroller.marshalFormSections(); {~P~}.views['{~D:Context[0].Hash~}'].render();">
            <span class="icon is-small"><i class="fas fa-fw fa-calculator" aria-hidden="true"></i></span>
            <span>Solve</span>
        </button>
        <div class="control">
            <span class="PSFS-Filter-Icon icon"><i class="fas fa-fw fa-magnifying-glass" aria-hidden="true"></i></span>
            <input id="PSFS-Filter-Input" type="text" class="PSFS-Filter-Input"
                placeholder="Filter logs..."
                aria-label="Filter solver logs"
                value="{~D:Context[0].FilterQuery~}"
                oninput="{~P~}.views.PictFormsSolver.applyFilter(this.value)" />
            <button type="button" class="PSFS-Filter-Clear" title="Clear filter"
                onclick="{~P~}.views.PictFormsSolver.clearFilter()">
                <span class="icon is-small"><i class="fas fa-fw fa-xmark" aria-hidden="true"></i></span>
            </button>
        </div>
        <span id="PSFS-Filter-Counter" class="PSFS-Filter-Counter" aria-live="polite">
            <span class="icon"><i class="fas fa-fw fa-filter" aria-hidden="true"></i></span>
            <span id="PSFS-Filter-Counter-Text"></span>
        </span>
    </div>
    {~TS:HL-Pict-Form-Solver-Entries:Context[0].getDynamicState().Solvers~}
    {~TS:HL-Pict-Form-Solver-CustomLogWrap:Context[0].CustomLogSectionsSorted~}
    <div id="PSFS-Filter-Empty" class="PSFS-Filter-Empty">No logs match your filter.</div>
</div>
`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-Entries',
				Template: /*html*/`
    <div id="PSFExt-Section-{~D:Record.Hash~}" class="PSFExt-Section">
        <h3 class="PSFExt-Section-Header">
            <span class="is-flex is-align-items-center is-flex-grow-1">
                <span class="PSFExt-DeEmphasize">{~D:Record.ExpressionScope~}</span> {~D:Record.SectionOrdinal~}.G.{~D:Record.GroupOrdinal~}
                <span class="PSFExt-DeEmphasize">Ord</span>{~D:Record.Ordinal~}
                <span class="PSFExt-DeEmphasize">Ind</span>{~D:Record.Index~}
            </span>
            <span class="is-flex is-align-items-center ml-auto">
                <a class="button is-small is-dark mr-1" href="javascript:void(0);"
                    onclick="{~P~}.ContentAssignment.toggleClass('#PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExpressionContainer', 'PSFExt-Hidden');">
                    <span class="icon is-small"><i class="fas fa-fw fa-pencil" aria-hidden="true"></i></span>
                    <span>Edit</span>
                </a>
                <a class="button is-small is-dark" href="javascript:void(0);"
                    onclick="{~P~}.ContentAssignment.toggleClass('#PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExtraDataContainer', 'PSFExt-Hidden');">
                    <span class="icon is-small"><i class="fas fa-fw fa-circle-info" aria-hidden="true"></i></span>
                    <span>More</span>
                </a>
            </span>
        </h3>
        
        <div id="PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExpressionContainer" class="PSFExt-Hidden">
            <p class="PSFExt-Data">
                <textarea id="PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExpressionEditbox" class="PSFExt-ExpressionEditbox">{~D:Record.Expression~}</textarea>
            </p>
            <div class="buttons is-right p-2 m-0">
                <button type="button" class="button is-small is-primary"
                    onclick="
                        {~P~}.views['{~D:Context[0].Hash~}'].updateExpressionFromElement(
                            '#PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExpressionEditbox',
                            '{~D:Record.ExpressionScope~}', '{~D:Record.ViewHash~}', {~D:Record.SectionOrdinal~}, {~D:Record.Index~},
                            '#PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-Display'
                        );
                        {~P~}.ContentAssignment.addClass('#PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExpressionContainer', 'PSFExt-Hidden');
                    ">
                    <span class="icon is-small"><i class="fas fa-fw fa-check" aria-hidden="true"></i></span>
                    <span>Apply</span>
                </button>
            </div>
        </div>
        <div id="PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-Extra" class="PSFExt-Section-ExtraData">
            <p class="PSFExt-Solver-Entry" id="PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-Display">{~D:Record.Expression~}</p>
            <p class="PSFExt-Data">
                <span class="PSFExt-Label">Value</span> {~TBT:Record.Value:object,array:HL-Pict-Form-RecordValueDisplayComplex:Record:HL-Pict-Form-RecordValueDisplaySimple~}
            </p>
        </div>
        <div id="PSFExt-{~D:Record.ExpressionScope~}-{~D:Record.ViewHash~}-S{~D:Record.SectionOrdinal~}-G{~D:Record.GroupOrdinal~}-{~D:Record.Index~}-ExtraDataContainer" class="PSFExt-Section-ExtraData PSFExt-Hidden">
            <h5 class="PSFExt-Content-Header PSFExt-Section-Group-Header">{~D:Record.LastResultsObject.PostfixTokenObjects.length~} Postfix Tokens</h5>
            {~TS:HL-Pict-Form-Solver-PostfixToken:Record.LastResultsObject.PostfixTokenObjects~}
            <h5 class="PSFExt-Content-Header PSFExt-Section-Group-Header">Virtual Symbols</h5>
            {~TVS:HL-Pict-Form-Solver-VirtualSymbols:Record.LastResultsObject.VirtualSymbols~}
            <h5 class="PSFExt-Content-Header PSFExt-Section-Group-Header">{~D:Record.LastResultsObject.PostfixTokenObjects.length~} Postfix Solve List</h5>
            {~TVS:HL-Pict-Form-Solver-PostFixSolveEntry:Record.LastResultsObject.PostfixSolveList~}
        </div>
    </div>
`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-PostfixToken',
				Template: /*html*/`
            <p class="PSFExt-Data">
                <span class="PSFExt-Label">{~D:Record.Type~}</span> <span class="PSFExt-Token">{~D:Record.Token~}</span>
            </p>
`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-VirtualSymbols',
				Template: /*html*/`
            <p class="PSFExt-Data">
                <span class="PSFExt-Label">{~D:Record.Key~}</span> {~TBT:Record.Value:object,array:HL-Pict-Form-RecordValueDisplayComplex:Record:HL-Pict-Form-RecordValueDisplaySimple~}
            </p>
`,
			},
			{
				Hash: 'HL-Pict-Form-RecordValueDisplayComplex',
				Template: /*html*/`
            <!-- Complex Value -->
            {~TBT:Record.Value:array:HL-Pict-Form-RecordValueDisplayArray:Record:HL-Pict-Form-RecordValueDisplayObject~}
`,
			},
			{
				Hash: 'HL-Pict-Form-RecordValueDisplayObject',
				Template: /*html*/`JSON Object
            <!-- RAW Object
{~DJ:Record.Value~}
            -->`,
			},
			{
				Hash: 'HL-Pict-Form-RecordValueDisplayArray',
				Template: /*html*/`JSON Array ({~D:Record.Value.length~} items)
            <!-- RAW Array
{~DJ:Record.Value~}
            -->`,
			},
			{
				Hash: 'HL-Pict-Form-RecordValueDisplaySimple',
				Template: /*html*/`{~D:Record.Value~}`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-PostFixSolveEntry',
				Template: /*html*/`
            <div>
                <h6 class="PSFExt-Content-Header PSFExt-Data-Header">Postfix Solve Entry #{~D:Record.Key~} Symbol <span class="PSFExt-Token">{~D:Record.Value.VirtualSymbolName~}</span></h6>
                <table class="PSFExt-Data-Table" style="width: 98%;">
                    <thead>
                        <tr>
                            <th width="15%">&nbsp;</th>
                            <th width="30%">Left</th>
                            <th width="25%">Operation</th>
                            <th width="30%">Right</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td class="cell-header">Virtual Symbol</td>
                            <td> {~D:Record.Value.LeftValue.VirtualSymbolName~} </td>
                            <td> {~D:Record.Value.Operation.VirtualSymbolName~} </td>
                            <td> {~D:Record.Value.RightValue.VirtualSymbolName~} </td>
                        </tr>
                        <tr>
                            <td class="cell-header">Value</td>
                            <td> {~TBT:Record.Value.LeftValue.Value:object,array:HL-Pict-Form-RecordValueDisplayComplex:Record.Value.LeftValue:HL-Pict-Form-RecordValueDisplaySimple~} </td>
                            <td>&nbsp;</td>
                            <td> {~TBT:Record.Value.RightValue.Value:object,array:HL-Pict-Form-RecordValueDisplayComplex:Record.Value.RightValue:HL-Pict-Form-RecordValueDisplaySimple~} </td>
                        </tr>
                        <tr>
                            <td class="cell-header">Tokens</td>
                            <td> {~D:Record.Value.LeftValue.Token~} </td>
                            <td> {~D:Record.Value.Operation.Token~} </td>
                            <td> {~D:Record.Value.RightValue.Token~} </td>
                        </tr>
                        <tr>
                            <td class="cell-header">Type</td>
                            <td> {~D:Record.Value.LeftValue.Type~} </td>
                            <td> {~D:Record.Value.Operation.Type~} </td>
                            <td> {~D:Record.Value.RightValue.Type~} </td>
                        </tr>
                        <tr>
                            <td class="cell-header">Solve Layer</td>
                            <td> {~D:Record.Value.LeftValue.SolveLayerStack~} </td>
                            <td>&nbsp;</td>
                            <td> {~D:Record.Value.RightValue.SolveLayerStack~} </td>
                        </tr>
                    </tbody>
                </table>
            </div>
`,
			},
			// ── Custom Solver Log templates ──────────────────────────────
			{
				Hash: 'HL-Pict-Form-Solver-CustomLogWrap',
				Template: /*html*/`
    <div class="PSFS-CustomLog-Header">
        <span class="icon"><i class="fas fa-fw fa-scroll" aria-hidden="true"></i></span>
        Custom Solver Logs
    </div>
    {~TS:HL-Pict-Form-Solver-CustomLogSection:Record.Sections~}
`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-CustomLogSection',
				Template: /*html*/`
    <div class="hl-log-section">
        <div class="hl-log-section-header"
             onclick="{~P~}.views.PictFormsSolver.toggleCustomLogSection('{~D:Record.Key~}')">
            <span class="hl-log-section-icon icon">
                <i class="fas fa-fw fa-list-ol" aria-hidden="true"></i>
            </span>
            <span class="hl-log-section-name">{~D:Record.Name~}</span>
            <span class="hl-log-section-badge">{~D:Record.Entries.length~}</span>
            <span class="hl-log-section-chevron icon">
                <i class="fas fa-fw {~D:Record.ChevronIconClass~}" data-custom-log-chevron="{~D:Record.Key~}" aria-hidden="true"></i>
            </span>
        </div>
        <div data-custom-log-section="{~D:Record.Key~}" class="hl-log-section-body {~D:Record.HiddenClass~}">
            {~TS:HL-Pict-Form-Solver-CustomLogEntry:Record.Entries~}
        </div>
    </div>
`,
			},
			{
				Hash: 'HL-Pict-Form-Solver-CustomLogEntry',
				Template: /*html*/`{~D:Record.Html~}`,
			},
		],
		Renderables: [
			{
				RenderableHash: 'HL-Pict-Form-Solver-Renderable',
				TemplateHash: 'HL-Pict-Form-Solver-Content',
			},
		],
	});

/**
 * @typedef {Object} CustomLogEntry
 * @property {string} Type       - 'Entry' or 'Calculation'
 * @property {string} Category   - Section key used for grouping
 * @property {string} Label      - Display label (section display name)
 * @property {string} Message    - Human-readable message / description
 * @property {string} Severity   - 'Trace' | 'Info' | 'Warning' | 'Error'
 * @property {string=} Result    - Calculated value (Calculation entries only)
 * @property {string=} Formula   - Expression string (Calculation entries only)
 * @property {string=} FormulaDescription - Variable explanation (Calculation entries only)
 * @property {string} Html       - Pre-rendered HTML for the template
 */

/**
 * @typedef {Object} CustomLogSection
 * @property {string} Key
 * @property {string} Name
 * @property {CustomLogEntry[]} Entries
 * @property {boolean} Collapsed
 * @property {string} HiddenClass
 */

// @ts-ignore
class PictFormsSolver extends libPictSectionForm.PictFormSupportBase
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this.DisplayShortName = 'HL-SV';
		this.DisplayLongName = 'Headlight-SolverVisualization';

		/** @type {CustomLogSection[]} */
		this._customLogSections = [];

		// Active filter query applied across both the standard Pict
		// solver entries and the custom log sections. Held on the
		// instance (rather than the DOM) so the filter survives every
		// re-render — onAfterRender re-applies it to the freshly
		// rendered nodes. Cleared via clearFilter() or by emptying
		// the input.
		/** @type {string} */
		this._filterQuery = '';
	}

	/**
	 * Current filter query string. Exposed as a getter so the
	 * content template can hydrate the input's `value` attribute on
	 * each render via `{~D:Context[0].FilterQuery~}`.
	 * @return {string}
	 */
	get FilterQuery()
	{
		return this._filterQuery;
	}

	// ── Custom Solver Log API ────────────────────────────────────────

	/**
	 * Sorted snapshot of sections for template consumption.
	 * Exposed as `CustomLogSectionsSorted` on the view instance so the
	 * content template can iterate it via `{~TS:...:Context[0].CustomLogSectionsSorted~}`.
	 *
	 * Returns an array containing a single wrapper object whose `.Sections`
	 * property holds the sorted section list — this matches the `{~TS:...~}`
	 * pattern (iterates an array, each Record has `.Sections`).
	 *
	 * @return {Array<{ Sections: CustomLogSection[] }>}
	 */
	get CustomLogSectionsSorted()
	{
		if (this._customLogSections.length === 0)
		{
			return [];
		}
		const sorted = this._customLogSections.slice().sort((a, b) => a.Key.localeCompare(b.Key));
		// Pre-compute presentation state for each section
		for (const section of sorted)
		{
			section.HiddenClass = section.Collapsed ? 'is-hidden' : '';
			section.ChevronIconClass = section.Collapsed ? 'fa-chevron-right' : 'fa-chevron-down';
		}
		return [ { Sections: sorted } ];
	}

	/**
	 * Find or create a section by key.
	 *
	 * @param {string} pSectionKey
	 * @param {string=} pDisplayName - Human-readable name; defaults to pSectionKey.
	 * @return {CustomLogSection}
	 * @private
	 */
	_getOrCreateSection(pSectionKey, pDisplayName)
	{
		let section = this._customLogSections.find((s) => s.Key === pSectionKey);
		if (!section)
		{
			section = { Key: pSectionKey, Name: pDisplayName || pSectionKey, Entries: [], Collapsed: false, HiddenClass: '' };
			this._customLogSections.push(section);
		}
		return section;
	}

	/**
	 * Parse a formula description string and render variable names as
	 * styled chip elements with operators as plain muted text between them.
	 *
	 * Input strings look like: `TotalWorkCompletedAmount &plus; SubtotalAmount`
	 * where operators are HTML entities (`&plus;`, `&times;`, `&minus;`,
	 * `&divide;`, `&equals;`).
	 *
	 * @param {string} pFormulaDescription - Variable names separated by HTML entity operators
	 * @return {string} HTML string with chip-wrapped variables and operator spans
	 * @private
	 */
	_renderFormulaChips(pFormulaDescription)
	{
		if (!pFormulaDescription)
		{
			return '';
		}

		// Split on HTML entity operators, keeping the delimiters
		const splitPattern = /(&plus;|&times;|&minus;|&divide;|&equals;)/g;
		// Non-global regex for classifying each token (avoids lastIndex drift)
		const isOperator = /^&(?:plus|times|minus|divide|equals);$/;
		const parts = pFormulaDescription.split(splitPattern);

		let html = '';
		for (const part of parts)
		{
			const trimmed = part.trim();
			if (!trimmed)
			{
				continue;
			}

			if (isOperator.test(trimmed))
			{
				html += `<span class="PSFS-CustomLog-Chip-Op">${trimmed}</span>`;
			}
			else
			{
				html += `<span class="PSFS-CustomLog-Chip">${trimmed}</span>`;
			}
		}
		return html;
	}

	/**
	 * Map a severity string to a CSS modifier class.
	 *
	 * @param {string=} pSeverity
	 * @return {string}
	 * @private
	 */
	_severityClass(pSeverity)
	{
		const key = (pSeverity || 'trace').toLowerCase();
		if (key === 'error') return 'severity-error';
		if (key === 'warning') return 'severity-warning';
		if (key === 'info') return 'severity-info';
		return 'severity-trace';
	}

	/**
	 * Add a general log entry. Signature matches the legacy
	 * `Headlight-Section-SolverLog.addEntry()` API so existing call sites
	 * can be routed here without modification.
	 *
	 * @param {string} pCategory     - Section key used for grouping (e.g. '001PaymentObservationQuantities')
	 * @param {string} pLabel        - Section display name (e.g. 'Payment Quantities')
	 * @param {string} pMessage      - Human-readable log message
	 * @param {string=} pSeverity    - 'Trace' | 'Info' | 'Warning' | 'Error'
	 */
	addEntry(pCategory, pLabel, pMessage, pSeverity)
	{
		const section = this._getOrCreateSection(pCategory, pLabel);
		const sevClass = this._severityClass(pSeverity);
		const sevLabel = (pSeverity || 'Trace');

		const html = /*html*/`
		<div class="PSFS-CustomLog-Entry">
			<span class="PSFS-CustomLog-Entry-Severity ${sevClass}">${sevLabel}</span>
			${pMessage}
		</div>`;

		section.Entries.push({
			Type: 'Entry',
			Category: pCategory,
			Label: pLabel,
			Message: pMessage,
			Severity: sevLabel,
			Html: html,
		});
	}

	/**
	 * Add a calculation log entry. Signature matches the legacy
	 * `Headlight-Section-SolverLog.addCalculation()` API.
	 *
	 * @param {string} pCategory           - Section key
	 * @param {string} pLabel              - Section display name
	 * @param {string} pDescription        - What is being calculated
	 * @param {string|number} pResult      - Computed value
	 * @param {string=} pFormula           - Expression string
	 * @param {string=} pFormulaDescription - Human-readable variable breakdown
	 */
	addCalculation(pCategory, pLabel, pDescription, pResult, pFormula, pFormulaDescription)
	{
		const section = this._getOrCreateSection(pCategory, pLabel);

		// Build the formula line with variable chips
		let formulaHtml = '';
		if (pFormulaDescription)
		{
			const chips = this._renderFormulaChips(pFormulaDescription);
			formulaHtml += /*html*/`<div class="PSFS-CustomLog-Calc-Formula">${chips}</div>`;
		}
		// Show the numeric expression on its own quieter line
		if (pFormula)
		{
			formulaHtml += /*html*/`<div class="PSFS-CustomLog-Calc-NumericFormula">${pFormula}</div>`;
		}

		const html = /*html*/`
		<div class="PSFS-CustomLog-Calc">
			<span>${pDescription}</span>
			<span class="PSFS-CustomLog-Calc-Result">= ${pResult}</span>
			${formulaHtml}
		</div>`;

		section.Entries.push({
			Type: 'Calculation',
			Category: pCategory,
			Label: pLabel,
			Message: pDescription,
			Severity: 'Info',
			Result: String(pResult),
			Formula: pFormula || '',
			FormulaDescription: pFormulaDescription || '',
			Html: html,
		});
	}

	/**
	 * Clear all custom log sections. Intended to be called at the start
	 * of each solve cycle so stale entries don't accumulate.
	 */
	resetEntries()
	{
		this._customLogSections = [];
	}

	/**
	 * Toggle the collapsed state of a custom log section.
	 * Called from the section header onclick in the template.
	 *
	 * @param {string} pSectionKey
	 */
	toggleCustomLogSection(pSectionKey)
	{
		const section = this._customLogSections.find((s) => s.Key === pSectionKey);
		if (!section) return;
		section.Collapsed = !section.Collapsed;
		// Toggle the DOM directly for responsiveness — the next full
		// render will pick up the persisted Collapsed flag.
		const el = document.querySelector(`[data-custom-log-section="${pSectionKey}"]`);
		if (el)
		{
			el.classList.toggle('is-hidden');
		}
		// Flip the chevron icon to match
		const chevron = document.querySelector(`[data-custom-log-chevron="${pSectionKey}"]`);
		if (chevron)
		{
			chevron.classList.toggle('fa-chevron-down', !section.Collapsed);
			chevron.classList.toggle('fa-chevron-right', section.Collapsed);
		}
	}

	/**
	 * Trigger a render of the custom log portion. Forms can call this
	 * explicitly after a batch of addEntry/addCalculation calls to
	 * update the panel in one shot rather than per-entry.
	 */
	renderCustomLogs()
	{
		this.render();
	}

	// ── Filter / highlight ──────────────────────────────────────────

	/**
	 * Apply the filter query across all standard solver entries and
	 * custom log sections / entries — hides non-matches and wraps
	 * matched substrings in `<mark>` tags for browser-Find-style
	 * highlighting. Called from the search input's `oninput` handler,
	 * so it runs synchronously against the rendered DOM and the user
	 * sees feedback as they type. The active query is stored on the
	 * instance so it survives re-renders.
	 *
	 * Matching is multi-token substring: the query is split on
	 * whitespace and every token must appear (case-insensitively) as
	 * a literal substring of the target text.
	 *
	 * @param {string} pQuery
	 */
	applyFilter(pQuery)
	{
		this._filterQuery = (pQuery == null ? '' : String(pQuery)).trim();
		this._refreshFilterDOM();
	}

	/**
	 * Clear the active filter and restore visibility of every entry.
	 * Wired to the inline (×) button in the filter bar.
	 */
	clearFilter()
	{
		this._filterQuery = '';
		const tmpInput = document.getElementById('PSFS-Filter-Input');
		if (tmpInput)
		{
			tmpInput.value = '';
			tmpInput.focus();
		}
		this._refreshFilterDOM();
	}

	/**
	 * Walk the rendered solver content and update three things based
	 * on the active filter query:
	 *
	 *   • Visibility — hide entries / sections that don't match.
	 *   • Highlighting — wrap matched substrings in `<mark>` tags so
	 *     the user can see exactly what landed (browser-Find style).
	 *   • Counter + empty state — refresh the right-side count
	 *     ("12 / 47") and toggle the "no logs match" banner.
	 *
	 * Visibility cascades through three layers:
	 *
	 *   1. `.PSFExt-Section` — Pict-rendered solver expression blocks.
	 *      Each is a single coherent unit (header + expression display
	 *      + extra data), matched as a whole by its full text content.
	 *   2. `.hl-log-section` — custom log collapsible groups. Match
	 *      hierarchically: if the section header text matches, every
	 *      entry stays visible; otherwise entries are filtered
	 *      individually and the group hides only when nothing inside
	 *      matches.
	 *   3. The "Custom Solver Logs" wrap header — hides when nothing
	 *      inside it survives, so the user isn't left with an orphan
	 *      heading floating above filtered-away content.
	 *
	 * Sections are hidden via the dedicated `PSFS-Filter-Hidden` class
	 * (not Bulma's `is-hidden`) so the user's manual collapse state on
	 * a custom log section remains independent of the filter — toggling
	 * the chevron and toggling the filter don't fight each other.
	 *
	 * @private
	 */
	_refreshFilterDOM()
	{
		const tmpRoot = document.getElementById('Pict-Form-Solver-Content');
		if (!tmpRoot)
		{
			return;
		}
		const tmpTokens = this._tokenizeFilterQuery(this._filterQuery);
		const tmpHasQuery = tmpTokens.length > 0;
		const tmpFilterBar = document.getElementById('PSFS-Filter-Bar');
		const tmpEmptyMsg = document.getElementById('PSFS-Filter-Empty');
		const tmpCounterText = document.getElementById('PSFS-Filter-Counter-Text');
		if (tmpFilterBar)
		{
			tmpFilterBar.classList.toggle('is-active', tmpHasQuery);
		}

		// Strip last cycle's <mark> highlights before re-evaluating. We
		// do this up front so the textContent reads we use for matching
		// (and any subsequent re-highlight pass) operate on a clean DOM
		// — otherwise mark wrappers from a previous keystroke would
		// accumulate.
		this._clearHighlights(tmpRoot);

		// Pre-build the multi-token highlight regex once per call so we
		// don't compile it inside the inner loop. Null when there's no
		// active query — the per-entry highlight pass becomes a no-op.
		const tmpHighlightRegex = tmpHasQuery ? this._buildHighlightRegex(tmpTokens) : null;

		// Tally counts as we go so the on-screen counter ("12 / 47")
		// reflects the same leaf-level definition the user perceives:
		// each standard solver expression block counts as 1, and each
		// custom log entry row inside a collapsible group counts as 1.
		let tmpVisibleCount = 0;
		let tmpTotalCount = 0;

		// Standard Pict-rendered solver expressions
		const tmpPsfSections = tmpRoot.querySelectorAll('.PSFExt-Section');
		for (const tmpSection of tmpPsfSections)
		{
			tmpTotalCount++;
			const tmpText = (tmpSection.textContent || '').toLowerCase();
			const tmpMatch = !tmpHasQuery || this._matchesAllTokens(tmpTokens, tmpText);
			tmpSection.classList.toggle('PSFS-Filter-Hidden', !tmpMatch);
			if (tmpMatch)
			{
				tmpVisibleCount++;
				if (tmpHighlightRegex)
				{
					this._applyHighlightsTo(tmpSection, tmpHighlightRegex);
				}
			}
		}

		// Custom Solver Log sections (collapsible groups)
		const tmpLogSections = tmpRoot.querySelectorAll('.hl-log-section');
		for (const tmpSection of tmpLogSections)
		{
			const tmpHeaderEl = tmpSection.querySelector('.hl-log-section-name');
			const tmpHeaderText = (tmpHeaderEl ? tmpHeaderEl.textContent || '' : '').toLowerCase();
			const tmpHeaderMatch = !tmpHasQuery || this._matchesAllTokens(tmpTokens, tmpHeaderText);
			let tmpSectionHasMatch = tmpHeaderMatch;

			// Highlight the header itself when it matches — gives the
			// user immediate visual confirmation of which group is
			// pinned by the section-name match path.
			if (tmpHighlightRegex && tmpHeaderEl && tmpHeaderMatch)
			{
				this._applyHighlightsTo(tmpHeaderEl, tmpHighlightRegex);
			}

			const tmpEntries = tmpSection.querySelectorAll('.hl-log-section-body > *');
			for (const tmpEntry of tmpEntries)
			{
				tmpTotalCount++;
				const tmpEntryText = (tmpEntry.textContent || '').toLowerCase();
				// When the section header itself matches, all entries
				// stay visible — the user is exploring the whole group.
				const tmpEntryMatch = tmpHeaderMatch || !tmpHasQuery || this._matchesAllTokens(tmpTokens, tmpEntryText);
				tmpEntry.classList.toggle('PSFS-Filter-Hidden', !tmpEntryMatch);
				if (tmpEntryMatch)
				{
					tmpSectionHasMatch = true;
					tmpVisibleCount++;
					if (tmpHighlightRegex)
					{
						this._applyHighlightsTo(tmpEntry, tmpHighlightRegex);
					}
				}
			}
			tmpSection.classList.toggle('PSFS-Filter-Hidden', !tmpSectionHasMatch);
		}

		// Hide the "Custom Solver Logs" wrap header when nothing inside
		// it survives the filter, so the user isn't left with an orphan
		// section heading floating above filtered-away content.
		const tmpCustomLogHeader = tmpRoot.querySelector('.PSFS-CustomLog-Header');
		if (tmpCustomLogHeader)
		{
			const tmpAnyLogVisible = Array.from(tmpLogSections).some((s) => !s.classList.contains('PSFS-Filter-Hidden'));
			tmpCustomLogHeader.classList.toggle('PSFS-Filter-Hidden', tmpHasQuery && !tmpAnyLogVisible);
		}

		if (tmpEmptyMsg)
		{
			tmpEmptyMsg.classList.toggle('is-active', tmpHasQuery && tmpVisibleCount === 0);
		}

		if (tmpCounterText)
		{
			if (tmpHasQuery)
			{
				tmpCounterText.textContent = `${tmpVisibleCount} / ${tmpTotalCount}`;
			}
			else
			{
				tmpCounterText.textContent = `${tmpTotalCount} log${tmpTotalCount === 1 ? '' : 's'}`;
			}
		}
	}

	/**
	 * Tokenize a filter query into the whitespace-separated lowercase
	 * words used as substring needles. Computed once per filter pass
	 * (not on every target match) so a long log list filters in
	 * O(targets * tokens) rather than re-splitting per row.
	 *
	 * @param {string} pQuery
	 * @return {string[]}
	 * @private
	 */
	_tokenizeFilterQuery(pQuery)
	{
		if (!pQuery)
		{
			return [];
		}
		const tmpTokens = [];
		for (const tmpRaw of String(pQuery).toLowerCase().split(/\s+/))
		{
			if (tmpRaw)
			{
				tmpTokens.push(tmpRaw);
			}
		}
		return tmpTokens;
	}

	/**
	 * Multi-word substring match. Every token in `pTokens` must appear
	 * as a literal (case-insensitive) substring of `pTarget`. Tokens
	 * may match in any order, but each one has to actually be in there
	 * — no character-anywhere-in-order looseness, which let "Result"
	 * pass for queries like "ConnectionsCalculationsLength".
	 *
	 * Single-token queries collapse to the equivalent of a normal
	 * browser-Find substring search; multi-token queries let users
	 * combine keywords ("calc width") without caring about ordering.
	 *
	 * `pTarget` must be pre-lowercased by the caller.
	 *
	 * @param {string[]} pTokens  Lowercased query tokens (non-empty).
	 * @param {string}   pTarget  Pre-lowercased target string.
	 * @return {boolean}
	 * @private
	 */
	_matchesAllTokens(pTokens, pTarget)
	{
		if (!pTokens || pTokens.length === 0)
		{
			return true;
		}
		if (!pTarget)
		{
			return false;
		}
		for (const tmpToken of pTokens)
		{
			if (pTarget.indexOf(tmpToken) === -1)
			{
				return false;
			}
		}
		return true;
	}

	/**
	 * Escape a literal string for safe inclusion in a RegExp source.
	 * Without this, query characters like `(`, `.`, `*`, `?`, `|` etc.
	 * would change the regex's meaning (the user types these often when
	 * filtering for things like `(VFE_4)` or `result=`).
	 *
	 * @param {string} pSource
	 * @return {string}
	 * @private
	 */
	_escapeRegex(pSource)
	{
		return pSource.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	}

	/**
	 * Build a single case-insensitive global regex that captures any
	 * occurrence of any token. Compiled once per filter call and reused
	 * across every visible entry — much cheaper than per-entry compile.
	 *
	 * The capture group is what `String.prototype.split` uses to keep
	 * the matched delimiters in the result array, which is the trick
	 * that drives the chunk-and-wrap loop in `_applyHighlightsTo`.
	 *
	 * @param {string[]} pTokens  Lowercased tokens (non-empty).
	 * @return {RegExp}
	 * @private
	 */
	_buildHighlightRegex(pTokens)
	{
		const tmpAlternation = pTokens.map((t) => this._escapeRegex(t)).join('|');
		return new RegExp(`(${tmpAlternation})`, 'gi');
	}

	/**
	 * Strip every previous-cycle highlight wrapper from `pRoot`. Called
	 * before each filter pass so highlights don't accumulate as the
	 * user keeps typing. Replaces each <mark> with its text content
	 * and normalizes the parent so adjacent text nodes coalesce.
	 *
	 * @param {ParentNode} pRoot
	 * @private
	 */
	_clearHighlights(pRoot)
	{
		if (!pRoot)
		{
			return;
		}
		const tmpMarks = pRoot.querySelectorAll('mark.PSFS-Filter-Highlight');
		const tmpDirtyParents = new Set();
		for (const tmpMark of tmpMarks)
		{
			const tmpParent = tmpMark.parentNode;
			if (!tmpParent)
			{
				continue;
			}
			tmpParent.replaceChild(document.createTextNode(tmpMark.textContent || ''), tmpMark);
			tmpDirtyParents.add(tmpParent);
		}
		for (const tmpParent of tmpDirtyParents)
		{
			if (tmpParent && typeof tmpParent.normalize === 'function')
			{
				tmpParent.normalize();
			}
		}
	}

	/**
	 * Wrap every match of `pRegex` inside `pRootEl` in a styled <mark>
	 * tag — same idea as the browser's Find highlight. Walks text
	 * nodes via TreeWalker so attributes, event handlers, and existing
	 * markup (icons, chips, severity tags, table cells) are preserved.
	 *
	 * Skips text inside <input>, <textarea>, <script>, <style>, and
	 * already-highlighted <mark> wrappers. Empty-value text nodes are
	 * filtered out as well; whitespace-only nodes pass through, but
	 * the regex won't match against them so they're effectively no-ops.
	 *
	 * The DOM mutation runs after the walk completes — modifying nodes
	 * during walking would invalidate the TreeWalker's cursor.
	 *
	 * @param {Element} pRootEl  Element whose descendant text gets highlighted.
	 * @param {RegExp}  pRegex   Global, case-insensitive regex with one capture group.
	 * @private
	 */
	_applyHighlightsTo(pRootEl, pRegex)
	{
		if (!pRootEl || !pRegex)
		{
			return;
		}
		const tmpWalker = document.createTreeWalker(
			pRootEl,
			NodeFilter.SHOW_TEXT,
			{
				acceptNode(pNode)
				{
					const tmpParent = pNode.parentNode;
					if (!tmpParent)
					{
						return NodeFilter.FILTER_REJECT;
					}
					const tmpTag = tmpParent.nodeName;
					if (tmpTag === 'INPUT' || tmpTag === 'TEXTAREA' || tmpTag === 'SCRIPT' || tmpTag === 'STYLE')
					{
						return NodeFilter.FILTER_REJECT;
					}
					if (tmpTag === 'MARK' && tmpParent.classList && tmpParent.classList.contains('PSFS-Filter-Highlight'))
					{
						return NodeFilter.FILTER_REJECT;
					}
					if (!pNode.nodeValue)
					{
						return NodeFilter.FILTER_REJECT;
					}
					return NodeFilter.FILTER_ACCEPT;
				},
			},
		);
		// Collect first, mutate after — TreeWalker doesn't tolerate
		// removing the current text node mid-walk.
		const tmpTextNodes = [];
		while (tmpWalker.nextNode())
		{
			tmpTextNodes.push(tmpWalker.currentNode);
		}
		for (const tmpNode of tmpTextNodes)
		{
			const tmpText = tmpNode.nodeValue;
			// Reset lastIndex so the global regex doesn't carry state
			// between text nodes (each split() resets it implicitly,
			// but being explicit keeps the intent obvious).
			pRegex.lastIndex = 0;
			if (!pRegex.test(tmpText))
			{
				continue;
			}
			pRegex.lastIndex = 0;
			const tmpParts = tmpText.split(pRegex);
			if (tmpParts.length <= 1)
			{
				continue;
			}
			const tmpFragment = document.createDocumentFragment();
			for (let tmpIndex = 0; tmpIndex < tmpParts.length; tmpIndex++)
			{
				const tmpChunk = tmpParts[tmpIndex];
				if (!tmpChunk)
				{
					continue;
				}
				// `String.split` with a capturing group emits the
				// captured matches at odd indices in the result array.
				if (tmpIndex % 2 === 1)
				{
					const tmpMark = document.createElement('mark');
					tmpMark.className = 'PSFS-Filter-Highlight';
					tmpMark.textContent = tmpChunk;
					tmpFragment.appendChild(tmpMark);
				}
				else
				{
					tmpFragment.appendChild(document.createTextNode(tmpChunk));
				}
			}
			const tmpParent = tmpNode.parentNode;
			if (tmpParent)
			{
				tmpParent.replaceChild(tmpFragment, tmpNode);
			}
		}
	}

	/**
	 * Re-apply the active filter and refresh the match counter after
	 * every render. Pict's render pipeline replaces the content
	 * wholesale on each solve cycle, so any filter state held only in
	 * DOM classes would be wiped — this hook reads from the persisted
	 * `_filterQuery` and re-toggles the filter classes against the
	 * freshly rendered nodes. Always runs (even with an empty filter)
	 * so the counter shows total log count by default.
	 *
	 * @param {any} pRenderable
	 * @return {boolean}
	 */
	onAfterRender(pRenderable)
	{
		const tmpResult = super.onAfterRender(pRenderable);
		this._refreshFilterDOM();
		return tmpResult;
	}
}
module.exports = PictFormsSolver;
module.exports.default_configuration = defaultViewConfiguration;
